import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/auth.js'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'

const PLAN_LABEL = { plus: 'Plus', researcher: 'Researcher', family: 'Researcher' }

export default function RegisterPage() {
  usePageTitle('Create account')
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { login } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Intent: where to land after register, and which plan (if any) the
  // user came here to subscribe to. Set by the landing pricing CTAs.
  const nextRaw = searchParams.get('next') || ''
  const plan = searchParams.get('plan')
  const planLabel = plan && PLAN_LABEL[plan]

  // Carry ?next + ?plan over to the sign-in link.
  const loginQs = new URLSearchParams()
  if (nextRaw) loginQs.set('next', nextRaw)
  if (plan) loginQs.set('plan', plan)
  const loginHref = `/login${loginQs.toString() ? '?' + loginQs.toString() : ''}`

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true); setError(null)
    try {
      const { token, user } = await api.post('/auth/register', form)
      login(token, user)
      if (nextRaw && nextRaw.startsWith('/')) {
        navigate(nextRaw)
      } else if (plan) {
        // Fallback — if no explicit next but a plan was selected, land on billing.
        navigate(`/billing?plan=${plan}`)
      } else {
        navigate('/')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Plan-aware tagline + CTA so users who clicked a paid plan don't see
  // "free forever" and feel misled.
  const tagline = planLabel
    ? `Create your account to continue to ${planLabel}.`
    : 'Start reliving lives. Free forever.'
  const submitLabel = loading
    ? 'Creating account…'
    : planLabel
      ? `Continue to ${planLabel}`
      : 'Create free account'

  return (
    <div className="auth-page">
      <div className="auth-card">
        <span className="auth-logo">Genealogic</span>
        <p className="auth-tagline">{tagline}</p>
        <form onSubmit={handleSubmit}>
          <label>
            Your name
            <input type="text" value={form.name} placeholder="Jan de Vries"
              onChange={e => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            Email
            <input type="email" value={form.email} placeholder="you@example.com"
              onChange={e => setForm({ ...form, email: e.target.value })} required />
          </label>
          <label>
            Password
            <input type="password" value={form.password} placeholder="Min. 8 characters"
              onChange={e => setForm({ ...form, password: e.target.value })} required minLength={8} />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" className="btn btn-gold" disabled={loading}>{submitLabel}</button>
        </form>
        <p className="auth-switch">
          Already have an account? <Link to={loginHref}>Sign in</Link>
        </p>
      </div>
      <a href="https://genealogic.ai" className="auth-back">← Back to genealogic.ai</a>
    </div>
  )
}
